import {
	Form,
	Row,
	Col,
	Input,
	DatePicker,
	Select,
	Button,
	Modal,
	InputNumber,
	Checkbox,
} from 'antd';
import styles from './ContentRating.module.less';
import FormItem from 'components/form/FormItem';
import api, {
	CreateHomescreenEventCommand,
	DownloadableContentDetailDto,
	ProductListItemDto,
	UpdateHomescreenEventCommand,
} from 'api';
import ImageFileUpload from 'components/ImageFileUpload';
import moment from 'moment-timezone';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { FileYupSchema, GetUploadLink, ImageFileModel } from 'utils/fileUtils';
import cx from 'classnames';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useLocation } from 'react-router-dom';
import OmniNotification from 'components/OmniNotification';
import {
	DLCTabName,
	IDownloadableContentDetailDto,
} from './EditDownloadableContent';

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		isConfirmedWithContentRating: yup.boolean().test(
			'isConfrimed',
			({ value }) =>
				t('downloadableContentEdit.tabs.contentRating.form.checkbox.error'),
			(value) => {
				return value == true;
			},
		),
	});
const { Option } = Select;

type Props = {
	id: string;
	data: IDownloadableContentDetailDto;
	onSave: (number: string) => void;
};

interface FormValues {
	isConfirmedWithContentRating: boolean;
}

export interface ContentRatingRef {
	validateContentRatingTab: () => void;
}

export default forwardRef(function ContentRating(props: Props, ref: any) {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const dispatch: AppDispatch = useDispatch();
	const history = useHistory();
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const { id, data, onSave } = props;
	const methods = useForm<FormValues>({
		mode: 'all',
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const {
		control,
		handleSubmit,
		setError,
		reset,
		formState,
		setValue,
		trigger,
	} = methods;

	useImperativeHandle(ref, () => ({
		async validateContentRatingTab() {
			return await trigger();
		},
	}));
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitButtonEnabled, SetSubmitButtonEnabled] = useState(true);
	const submitEvent = () => {
		handleSubmit(async (values: FormValues) => {
			setIsSubmitting(true);
			api
				.updateContentRatingForDLC({
					id: Number(id),
					confirmation: values.isConfirmedWithContentRating,
				})
				.then(() => {
					OmniNotification.success({
						message: t('downloadableContentEdit.message.update.success.title'),
						description: t(
							'downloadableContentEdit.message.update.success.description',
						),
					});
					SetSubmitButtonEnabled(false);
					onSave(DLCTabName.ContentRating);
				})
				.catch(() => {
					OmniNotification.error({
						message: t('notification.error.title'),
						description: t('api.defaultError'),
					});
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		})();
	};

	useEffect(() => {
		reset({
			isConfirmedWithContentRating: data.contentRatingConfirmation,
		});
	}, [data]);

	return (
		<>
			<FormProvider {...methods}>
				<Form data-testid="form" layout="vertical" onFinish={submitEvent}>
					<Row>
						<div style={{ flexDirection: 'row', alignItems: 'center' }}>
							<span className={styles.title}>
								{t('downloadableContentEdit.tabs.contentRating.title')}
							</span>
							<span className={styles.description}>
								{t('downloadableContentEdit.tabs.contentRating.description')}
							</span>
						</div>
					</Row>
					<Row
						style={{
							marginTop: 24,
						}}
					>
						<Col span={10}>
							<FormItem
								name="isConfirmedWithContentRating"
								control={control}
								render={({ field }) => (
									<Checkbox
										data-testid="is-confirmed-with-content-rating"
										checked={field.value}
										{...field}
										disabled={!submitButtonEnabled}
									>
										{t(
											'downloadableContentEdit.tabs.contentRating.form.checkbox.description',
										)}
									</Checkbox>
								)}
							/>
						</Col>
					</Row>
					<Button
						data-testid="content-rating-tab-save-btn"
						loading={isSubmitting}
						type="primary"
						htmlType="submit"
						size="large"
						disabled={!submitButtonEnabled}
					>
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
});
